import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toastSuccess } from '@components/Toast';

import ConfirmationModal from '@features/crm/components/ConfirmationModal';
import {
	allFilterPages,
	arkworkListFields,
	generateArtworkListParams
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import AddArtworkModal from '@features/assets/components/artwork/AddArtworkModal';
import ArtworkItem from '@features/assets/components/artwork/ArtworkItem';

import {
	addItemInGlobalFilterData,
	clearFilterResponse,
	handleFilter,
	removeItemFromFilterResponse,
	updateGlobalFilterData
} from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { deleteArtwork, getArtworkResponse } from '@services/assetsService';
import { fetchDependentFilterList } from '@services/commonService';

import { isPageReachBottom, printApiError } from '@utils/Util';

const ArtworkList = ({ isShowFilter, setTotalElements, showAddModal, setShowAddModal }) => {
	const [saveType, setSaveType] = useState('add');
	const [artworkList, setArtworkList] = useState(null);
	const [selectedArtworkId, setSelectedArtworkId] = useState(null);
	const [selectedIdToDelete, setSelectedIdToDelete] = useState(null);
	const [artworkResponse, setArtworkResponse] = useState(null);
	const [page, setPage] = useState(0);
	const filterResponse = useSelector((store) => store.filterResponseStore);
	let filterfields = useSelector((store) => store.filterFieldStore);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const sortValue = filterfields['sort'] && filterfields['sort'][0]?.key;
	const { ...paramsData } = generateArtworkListParams(filterfields);
	const dispatch = useDispatch();
	const scrollRef = useRef({});

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	useEffect(() => {
		setCurrentState(filterResponse);
		setArtworkList(filterResponse);
		setTotalElements(filterResponse.totalElements);
	}, [filterResponse]);

	useEffect(() => {
		window.scrollTo(0, 0);
		dispatch(openLoader());
		dispatch(fetchDependentFilterList({ type: 'ARTWORK_LIST' }, paramsData));
		setPage(0);
		let queryParams = generateArtworkListParams(filterfields, 0, sortValue);
		dispatch(handleFilter('getAllArtWork', queryParams, false)).finally(() => {
			dispatch(closeLoader());
		});
	}, [filterfields]);

	useEffect(() => {
		if (page !== 0) {
			dispatch(openLoader());
			let queryParams = generateArtworkListParams(filterfields, page, sortValue);
			dispatch(handleFilter('getAllArtWork', queryParams, true)).finally(() => {
				dispatch(closeLoader());
			});
		}
	}, [page]);

	const onActionSucces = (responseData) => {
		if (saveType === 'edit') {
			dispatch(updateGlobalFilterData(responseData));
		} else {
			dispatch(addItemInGlobalFilterData(responseData));
		}
		dispatch(fetchDependentFilterList({ type: 'ARTWORK_LIST' }, paramsData));
	};

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			dispatch(clearFilterResponse());
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = scrollRef.current;
			if (totalElements !== data?.length) {
				setPage(() => currentPage + 1);
			}
		}
	};

	const onEdit = async (id) => {
		setShowAddModal(true);
		setSelectedArtworkId(id);
		setSaveType('edit');
	};

	const onCloseModal = () => {
		setShowAddModal(false);
		setSelectedArtworkId(null);
		setArtworkResponse(null);
		setSaveType('');
	};

	const fetchArtworkData = async () => {
		if (selectedArtworkId) {
			try {
				dispatch(openLoader());
				const data = await getArtworkResponse(selectedArtworkId);
				setArtworkResponse(data);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const onCloseConfirmationModal = () => {
		setOpenConfirmation(false);
	};

	const OnDeleteArtwork = async () => {
		dispatch(openLoader());
		try {
			await deleteArtwork(selectedIdToDelete);
			dispatch(removeItemFromFilterResponse(selectedIdToDelete));
			dispatch(closeLoader());
			setOpenConfirmation(false);
			toastSuccess('Successfully deleted');
		} catch (error) {
			dispatch(closeLoader());
			printApiError(error);
		}
	};

	const onDeleteClick = (id) => {
		setSelectedIdToDelete(id);
		setOpenConfirmation(true);
	};

	useEffect(() => {
		fetchArtworkData();
	}, [selectedArtworkId]);

	const renderArtworkList = () => {
		if (artworkList?.data?.length > 0) {
			return artworkList.data.map((artwork, index) => {
				return (
					<ArtworkItem
						artwork={artwork}
						key={`artwork_item_${index}`}
						onEdit={onEdit}
						onDeleteClick={onDeleteClick}
					/>
				);
			});
		}
	};

	return (
		<>
			<section className="filter-data-container filter-v3-data-container under-inner-tabs bg-white">
				<FilterComponent
					filterPageName={allFilterPages.ARTWORK_LIST}
					fields={arkworkListFields}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>
				<div className="data-table">
					<div className="design-list-container">
						<div className="design-list-row">{renderArtworkList()}</div>
					</div>
				</div>
			</section>

			{showAddModal && (
				<AddArtworkModal
					artWorkResponse={artworkResponse}
					onCloseModal={onCloseModal}
					saveType={saveType}
					artworkId={selectedArtworkId}
					onActionSucces={onActionSucces}
				/>
			)}

			{openConfirmation && (
				<ConfirmationModal
					title={'Delete artwork'}
					closeModal={onCloseConfirmationModal}
					description={' about deleting this artwork?'}
					callback={OnDeleteArtwork}
					footerType={'CLOSE'}
				/>
			)}
		</>
	);
};

export default ArtworkList;
